import { BidderRule, byDefaultThenName, CreateBidderRule, UpdateBidderRule } from '@pubstack/common/src/bidder-rule'
import { BidderSeatProduct } from '@pubstack/common/src/bidder-seat'
import { buildUrlWithSearchParams } from '~/utils/url'
import { MutationToasts, useMutation, useQuery } from './api-client'

export const BIDDER_RULES_QUERY_KEYS = ['bidder-rules']

/**
 * One collection serves both products, but the endpoint is still mounted under each so the request
 * carries the caller's product. It goes into the cache key as well as the URL — two products asking
 * on the same scope must not share a cached response.
 */
const urlFor = (product: BidderSeatProduct) => `/${product}/bidder-rules`

/** The order the API answers in, kept wherever the list lands. */
const defaultsFirstThenByName = (rules: readonly BidderRule[]): BidderRule[] => [...rules].sort(byDefaultThenName)

//-------------------------------------------------------------------------
// Queries
//-------------------------------------------------------------------------
/** `archived: true` asks for the set-aside rules instead of the live ones. Owner-only at the API. */
export const useBidderRules = (product: BidderSeatProduct, withStacksUse = false, enabled = true, archived = false) => {
  const url = buildUrlWithSearchParams(urlFor(product), { ...(withStacksUse ? { withStacksUse } : {}), ...(archived ? { archived } : {}) })
  const query = useQuery<BidderRule[]>({ url, keys: [product, ...BIDDER_RULES_QUERY_KEYS, { withStacksUse, archived }], enabled, select: defaultsFirstThenByName })
  return { ...query, data: query.data || [] }
}

export const useBidderRule = (product: BidderSeatProduct, id?: string, enabled: boolean = !!id) => {
  return useQuery<BidderRule>({
    url: `${urlFor(product)}/${id}`,
    keys: [product, ...BIDDER_RULES_QUERY_KEYS, id || ''],
    enabled,
  })
}

//-------------------------------------------------------------------------
// Mutations
//-------------------------------------------------------------------------
export const useCreateBidderRule = (product: BidderSeatProduct, toasts: MutationToasts = {}) => {
  return useMutation<CreateBidderRule, BidderRule>({
    method: 'post',
    url: urlFor(product),
    collectionKeys: [product, ...BIDDER_RULES_QUERY_KEYS],
    ...toasts,
  })
}

export const useUpdateBidderRule = (product: BidderSeatProduct, id: string, toasts: MutationToasts = {}) => {
  return useMutation<UpdateBidderRule, BidderRule>({
    method: 'put',
    url: `${urlFor(product)}/${id}`,
    collectionKeys: [product, ...BIDDER_RULES_QUERY_KEYS],
    resourceId: id,
    ...toasts,
  })
}

/**
 * Set a rule aside, or bring it back. Owner-only at the API, and refused while any stack still uses it.
 *
 * `collectionKeys` covers the live list and the archived one alike — they share a prefix — so a rule
 * leaving one and appearing in the other refetches both.
 */
export const useArchiveBidderRule = (product: BidderSeatProduct, toasts: MutationToasts = {}) => {
  return useMutation<{ id: string; archived: boolean }, BidderRule>({
    method: 'put',
    url: ({ id }) => `${urlFor(product)}/${id}/archive`,
    collectionKeys: [product, ...BIDDER_RULES_QUERY_KEYS],
    ...toasts,
  })
}
